import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, ArrowUp, Minus } from "lucide-react";
import type { Property } from "@/lib/property";
import { LeadDialog } from "@/components/nor/LeadDialog";
import { useEvents } from "@/hooks/use-events";

type Msg = { role: "user" | "assistant"; content: string };
type Phase = "hidden" | "idle" | "active";

export function NorChatDock({ property: p }: { property: Property }) {
  const [phase, setPhase] = useState<Phase>("hidden");
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadKind, setLeadKind] = useState<"falar" | "visita">("falar");

  const scrollerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const { track } = useEvents();

  // Surge depois que o usuário começa a rolar
  useEffect(() => {
    function onScroll() {
      if (window.scrollY > 280) {
        setPhase((cur) => (cur === "hidden" ? "idle" : cur));
      }
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hidrata a conversa persistida desta sessão neste imóvel (F5 e navegação
  // SPA). A chave é o cookie assinado da sessão — nada de localStorage.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(
          `/api/public/pdp-chat?slug=${encodeURIComponent(p.id)}`,
        );
        if (!r.ok) return;
        const j = (await r.json()) as { messages?: Msg[] };
        if (!alive || !j.messages?.length) return;
        setMsgs((cur) => (cur.length ? cur : j.messages!));
        setPhase((cur) => (cur === "hidden" ? "idle" : cur));
      } catch {
        /* hidratação é best-effort */
      }
    })();
    return () => {
      alive = false;
    };
  }, [p.id]);

  // Auto-foco quando ativa
  useEffect(() => {
    if (phase === "active") setTimeout(() => inputRef.current?.focus(), 80);
  }, [phase]);

  // Auto-scroll da conversa
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, streaming]);

  function activate() {
    if (phase !== "active") {
      setPhase("active");
      track("ai_message", { property_slug: p.id, metadata: { kind: "pdp_chat_open" } });
    }
  }

  function maybeTriggerLead(text: string) {
    const low = text.toLowerCase();
    if (/(agend|visit|conhecer)/.test(low)) { setLeadKind("visita"); return true; }
    if (/(corretor|whats|telefone|falar com|proposta|contraproposta|negoc)/.test(low)) { setLeadKind("falar"); return true; }
    return false;
  }

  async function send() {
    const text = input.trim();
    if (!text || streaming) return;

    setError(null);
    const userMsg: Msg = { role: "user", content: text };
    const nextMsgs = [...msgs, userMsg];
    setMsgs([...nextMsgs, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    // Conteúdo da mensagem vive APENAS em conversation_messages.
    track("ai_message", { property_slug: p.id });

    const shouldOfferLead = maybeTriggerLead(text);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch("/api/public/pdp-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          messages: nextMsgs,
          property_slug: p.id,
          property: {
            code: p.code, title: p.title, bairro: p.bairro, cidade: p.cidade, tipo: p.tipo,
            preco: p.preco, area: p.area, dorms: p.dorms, suites: p.suites, vagas: p.vagas,
            andar: p.andar, pe_direito: p.pe_direito, orientacao: p.orientacao,
            condominio: p.condominio, iptu: p.iptu, proximidade: p.proximidade,
            why: p.why, highlights: p.highlights,
            agente: { nome: p.agente.nome, rating: p.agente.rating, deals: p.agente.deals },
          },
        }),
      });

      if (!resp.ok || !resp.body) {
        const errBody = await resp.json().catch(() => ({ error: "Falha na conexão" }));
        throw new Error(errBody.error ?? "Falha");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = ""; let acc = ""; let done = false;

      while (!done) {
        const { value, done: d } = await reader.read();
        if (d) break;
        buf += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buf.indexOf("\n")) !== -1) {
          let line = buf.slice(0, idx);
          buf = buf.slice(idx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line || line.startsWith(":")) continue;
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") { done = true; break; }
          try {
            const j = JSON.parse(data);
            const delta = j.choices?.[0]?.delta?.content;
            if (typeof delta === "string" && delta) {
              acc += delta;
              setMsgs((m) => {
                const copy = m.slice();
                copy[copy.length - 1] = { role: "assistant", content: acc };
                return copy;
              });
            }
          } catch {
            buf = line + "\n" + buf;
            break;
          }
        }
      }

      if (shouldOfferLead) setTimeout(() => setLeadOpen(true), 600);
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      setError((e as Error).message || "Falha ao conversar com o NOR");
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  if (phase === "hidden") return null;

  const isActive = phase === "active";
  const hasConversation = msgs.length > 0;

  return (
    <>
      {/* Wrapper centralizado no rodapé — largura/altura animam entre os 2 estados */}
      <div
        className={`fixed bottom-6 left-1/2 z-40 -translate-x-1/2 transition-all duration-300 ease-out ${
          isActive
            ? "w-[min(640px,calc(100vw-2rem))]"
            : "w-[min(440px,calc(100vw-2rem))] animate-fade-in"
        }`}
      >
        <div
          className={`nor-stroke flex flex-col overflow-hidden rounded-full border border-border/60 bg-background/70 shadow-2xl backdrop-blur-xl transition-[border-radius,background-color,max-height] duration-300 ${
            isActive ? "!rounded-2xl bg-background/95 max-h-[min(560px,70vh)]" : "max-h-[60px]"
          }`}
        >
          {/* Header só quando ativo */}
          {isActive && (
            <header className="flex shrink-0 items-center justify-between border-b border-border/60 px-4 py-2.5">
              <div className="flex items-center gap-2">
                <span className="relative grid h-6 w-6 place-items-center rounded-full bg-accent/15 text-accent">
                  <Sparkles className="h-3 w-3" />
                </span>
                <div className="leading-tight">
                  <div className="text-[12px] font-medium">NOR</div>
                  <div className="font-mono text-[8.5px] uppercase tracking-widest text-muted-foreground">{p.code}</div>
                </div>
              </div>
              <button
                onClick={() => setPhase("idle")}
                className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                aria-label="Minimizar"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
            </header>
          )}

          {/* Conversa (só quando ativo e tem msgs) */}
          {isActive && hasConversation && (
            <div ref={scrollerRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {msgs.map((m, i) => (
                <div key={i} className="space-y-1">
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                    {m.role === "user" ? "Você" : "NOR"}
                  </div>
                  {m.role === "assistant" ? (
                    <div className="prose prose-invert prose-sm max-w-none text-[13.5px] leading-relaxed text-foreground prose-p:my-2 prose-strong:text-foreground prose-ul:my-2 prose-li:my-0.5">
                      {m.content ? (
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      ) : (
                        <span className="inline-flex gap-1">
                          <span className="size-1 animate-soft-pulse rounded-full bg-accent" />
                          <span className="size-1 animate-soft-pulse rounded-full bg-accent [animation-delay:120ms]" />
                          <span className="size-1 animate-soft-pulse rounded-full bg-accent [animation-delay:240ms]" />
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-[13.5px] leading-relaxed text-foreground">{m.content}</p>
                  )}
                </div>
              ))}
              {error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-[12px] text-destructive">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Linha de input — sempre presente; vira CTA quando idle */}
          <div
            className={`flex items-center gap-2 ${
              isActive ? "px-3 py-2.5" : "px-2 py-2"
            }`}
            onClick={() => !isActive && activate()}
            role={!isActive ? "button" : undefined}
            tabIndex={!isActive ? 0 : undefined}
            onKeyDown={(e) => {
              if (!isActive && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                activate();
              }
            }}
          >
            <span
              className={`relative grid shrink-0 place-items-center rounded-full bg-accent/15 text-accent transition-all ${
                isActive ? "h-8 w-8 ml-1" : "h-9 w-9 ml-1"
              }`}
            >
              <Sparkles className={isActive ? "h-3.5 w-3.5" : "h-4 w-4"} />
              {!isActive && (
                <span className="absolute -right-0.5 -top-0.5 size-1.5 animate-soft-pulse rounded-full bg-accent ring-2 ring-background" />
              )}
            </span>

            {isActive ? (
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder={hasConversation ? "Continue a conversa…" : "Pergunte qualquer coisa sobre este imóvel…"}
                disabled={streaming}
                className="max-h-40 min-h-[36px] flex-1 resize-none bg-transparent px-1 py-1.5 text-[13.5px] outline-none placeholder:text-muted-foreground disabled:opacity-50"
              />
            ) : (
              <span className="flex-1 select-none truncate px-1 text-[13px] text-muted-foreground">
                Pergunte ao NOR sobre este imóvel…
              </span>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isActive) send();
                else activate();
              }}
              disabled={isActive && (!input.trim() || streaming)}
              className={`grid shrink-0 place-items-center rounded-full transition mr-1 ${
                isActive
                  ? input.trim() && !streaming
                    ? "h-8 w-8 bg-foreground text-background hover:opacity-90"
                    : "h-8 w-8 bg-secondary text-muted-foreground"
                  : "h-9 w-9 bg-foreground text-background hover:opacity-90"
              }`}
              aria-label={isActive ? "Enviar" : "Abrir chat NOR"}
            >
              <ArrowUp className={isActive ? "h-4 w-4" : "h-4 w-4"} />
            </button>
          </div>
        </div>

        {!isActive && (
          <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
            NOR · IA contextual deste imóvel
          </p>
        )}
      </div>

      <LeadDialog
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        propertySlug={p.id}
        source="pdp"
        agentName={p.agente.nome}
        conversation={msgs}
      />
    </>
  );
}
